// packages/web/src/components/marketplace/ServiceUnavailableWarning.tsx
// Warning screen when service is not available at user's address

import {
  LocationOff as LocationOffIcon,
  ShoppingCart as CartIcon,
  ArrowBack as ArrowBackIcon,
  EditLocation as EditLocationIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { useRouter } from 'next/router';
import * as React from 'react';

import { useAddress } from '../../contexts/AddressContext';

interface ServiceUnavailableWarningProps {
  service: {
    id: string;
    name: string;
    category: string;
    brand_name: string;
  };
  restrictions: {
    restriction_type: string;
    locations?: string[];
    coverage_areas?: string[];
    requires_verification?: boolean;
    requires_address_verification?: boolean;
  };
  checkedAddress: {
    street?: string;
    street_address?: string;
    city: string;
    state: string;
    zipCode?: string;
    zip_code?: string;
  };
  onChangeAddress?: () => void;
  onReturnToMarketplace?: () => void;
}

export default function ServiceUnavailableWarning({
  service,
  restrictions,
  checkedAddress,
  onChangeAddress,
  onReturnToMarketplace
}: ServiceUnavailableWarningProps) {
  const router = useRouter();
  const { setShowAddAddressDialog } = useAddress();

  const handleUseAvailableAddress = () => {
    // Open the shared address dialog (same as header's "Add Address" functionality)
    setShowAddAddressDialog(true);
  };

  const handleReturnToMarketplace = () => {
    if (onReturnToMarketplace) {
      onReturnToMarketplace();
    } else {
      // Default: navigate back to marketplace
      router.push('/');
    }
  };

  const formatRestrictionType = (type: string) => {
    switch (type) {
      case 'states': return 'Available in specific states';
      case 'counties': return 'Available in specific counties';
      case 'zip_codes': return 'Available in specific ZIP codes';
      case 'combined': return 'Available in specific areas';
      case 'everywhere': return 'Available everywhere';
      default: return 'Limited availability';
    }
  };

  // Get coverage areas with fallback
  const coverageAreas = restrictions?.coverage_areas || restrictions?.locations || [];
  const requiresVerification = restrictions?.requires_verification || restrictions?.requires_address_verification || false;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <LocationOffIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Service Not Available
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {service.name}
          </Typography>
          <Chip
            label={service.category}
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Current Address */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Your Selected Address</AlertTitle>
          <Typography variant="body2">
            {checkedAddress?.street_address || checkedAddress?.street || 'Address not available'}<br />
            {checkedAddress ? `${checkedAddress.city}, ${checkedAddress.state} ${checkedAddress.zip_code || checkedAddress.zipCode}` : ''}
          </Typography>
        </Alert>

        {/* Availability Information */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>{formatRestrictionType(restrictions?.restriction_type || 'unknown')}</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This service is only available in:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {coverageAreas.length > 0 ? (
              coverageAreas.map((area, index) => (
                <Chip
                  key={index}
                  label={area}
                  size="small"
                  variant="outlined"
                  color="warning"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Contact provider for availability details
              </Typography>
            )}
          </Box>
          {requiresVerification && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Address verification required for this service.
            </Typography>
          )}
        </Alert>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<ArrowBackIcon />}
            onClick={handleReturnToMarketplace}
            fullWidth
          >
            Find Available Services at Current Address
          </Button>

          <Button
            variant="outlined"
            size="large"
            startIcon={<EditLocationIcon />}
            onClick={handleUseAvailableAddress}
            fullWidth
          >
            Add New Address
          </Button>
        </Box>

        {/* Additional Info */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          Try selecting a different address or browse other services that may be available in your area.
        </Typography>
      </Paper>
    </Box>
  );
}